"use client"

import type React from "react"
import { useState } from "react"
import { useRouter } from "next/navigation"
import Link from "next/link"
import { useAuth } from "@/context/auth-context"
import {
  AuthShell,
  SubmitBtn,
  AltLink,
  MailIcon,
  LockIcon,
  EyeIcon,
} from "@/components/auth-shell"

export default function LoginPage() {
  const router = useRouter()
  const { login } = useAuth()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    setLoading(true)
    try {
      const result = await login(email, password)
      if (result.success) {
        router.push("/dashboard")
      } else {
        setError(result.message || "Login failed")
      }
    } catch {
      setError("An error occurred during login")
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthShell tag="ssh login@hsve.cc">
      <div style={{ textAlign: "center", marginBottom: 26 }}>
        <h1
          style={{
            fontSize: 28,
            fontWeight: 600,
            letterSpacing: "-0.025em",
            color: "var(--text)",
            margin: 0,
          }}
        >
          Welcome back
        </h1>
        <p style={{ fontSize: 14, color: "var(--text-2)", marginTop: 8 }}>
          Sign in to your HSVE account
        </p>
      </div>

      {error && (
        <div
          style={{
            marginBottom: 20,
            padding: "12px 14px",
            borderRadius: 6,
            border: "1px solid oklch(0.5 0.15 25 / 0.3)",
            background: "oklch(0.5 0.15 25 / 0.1)",
            fontSize: 13,
            color: "oklch(0.7 0.15 25)",
          }}
        >
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {/* Email field */}
          <div>
            <label
              style={{
                display: "block",
                fontSize: 13,
                fontWeight: 500,
                color: "var(--text-2)",
                marginBottom: 8,
              }}
            >
              Email
            </label>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                height: 46,
                background: "var(--bg)",
                border: "1px solid var(--border)",
                borderRadius: 6,
                overflow: "hidden",
              }}
            >
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 46,
                  color: "var(--muted)",
                }}
              >
                <MailIcon />
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                style={{
                  flex: 1,
                  height: "100%",
                  border: "none",
                  background: "transparent",
                  color: "var(--text)",
                  fontSize: 14,
                  outline: "none",
                  paddingRight: 14,
                }}
              />
            </div>
          </div>

          {/* Password field */}
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                marginBottom: 8,
              }}
            >
              <label
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: "var(--text-2)",
                }}
              >
                Password
              </label>
              <Link
                href="/forgot-password"
                className="font-mono"
                style={{
                  fontSize: 12,
                  color: "var(--accent)",
                  textDecoration: "none",
                }}
              >
                Forgot password?
              </Link>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                height: 46,
                background: "var(--bg)",
                border: "1px solid var(--border)",
                borderRadius: 6,
                overflow: "hidden",
              }}
            >
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 46,
                  color: "var(--muted)",
                }}
              >
                <LockIcon />
              </span>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                style={{
                  flex: 1,
                  height: "100%",
                  border: "none",
                  background: "transparent",
                  color: "var(--text)",
                  fontSize: 14,
                  outline: "none",
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 46,
                  height: "100%",
                  border: "none",
                  background: "transparent",
                  color: "var(--muted)",
                  cursor: "pointer",
                }}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <EyeIcon off={showPassword} />
              </button>
            </div>
          </div>

          <SubmitBtn loading={loading} loadingText="Signing in...">
            Sign in
          </SubmitBtn>
        </div>
      </form>

      <AltLink>
        Don&apos;t have an account?{" "}
        <Link
          href="/register"
          style={{ color: "var(--accent)", fontWeight: 500, textDecoration: "none" }}
        >
          Sign up
        </Link>
      </AltLink>
    </AuthShell>
  )
}
